import type { IProxy } from '@hit-game/shared/types';
import { Activity, Eye, EyeOff, Loader2 } from 'lucide-react';
import { useMemo, useState, type FormEvent } from 'react';

import { useToast } from '../../context/ToastContext';
import { accountService } from '../../services/account.service';
import { proxyService } from '../../services/proxy.service';
import { Dropdown } from '../common/Dropdown';
import { FIELD_CLASS_BOLD as FIELD_CLASS, LABEL_CLASS } from '../common/form.styles';

type ProxyHealth = 'alive' | 'dead' | 'unknown';

const HEALTH_LABEL: Record<ProxyHealth, string> = {
  alive: '🟢 Proxy còn sống',
  dead: '🔴 Proxy chết',
  unknown: '⚪ Chưa check',
};

const HEALTH_CLASS: Record<ProxyHealth, string> = {
  alive: 'text-success',
  dead: 'text-danger',
  unknown: '',
};

/** Dấu gắn sau tên proxy trong dropdown — cùng bộ 3 trạng thái với hai bảng trên. */
const HEALTH_MARK: Record<ProxyHealth, string> = {
  alive: ' 🟢',
  dead: ' 🔴',
  unknown: '',
};

interface AccountAddFormProps {
  proxies: IProxy[];
  onAdded: () => void;
}

/**
 * Đăng nhập tài khoản nhà cái rồi thêm vào hệ thống.
 *
 * Đây là đường DUY NHẤT để có tài khoản trong tool: chỉ khi nhà cái xác nhận
 * đăng nhập được thì bản ghi mới được tạo, nên không có tài khoản rác.
 */
export function AccountAddForm({ proxies, onAdded }: AccountAddFormProps) {
  const toast = useToast();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [proxy, setProxy] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [health, setHealth] = useState<Record<string, ProxyHealth>>({});
  const [checking, setChecking] = useState(false);

  // Proxy đã gán cho tài khoản khác thì không hiện — 1 proxy 1 tài khoản.
  const options = useMemo(
    () =>
      proxies
        .filter((item) => !item.accountId)
        .map((item) => ({
          value: item.value,
          label: `${item.value}${HEALTH_MARK[health[item.value] ?? 'unknown']}`,
        })),
    [proxies, health],
  );

  const checkProxy = () => {
    if (!proxy) return;
    setChecking(true);
    void proxyService
      .checkHealth([proxy])
      .then(([result]) => {
        setHealth((prev) => ({ ...prev, [proxy]: result?.alive ? 'alive' : 'dead' }));
      })
      .catch(() => {
        toast.error('Không kiểm tra được proxy');
      })
      .finally(() => {
        setChecking(false);
      });
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      await accountService.loginAndAdd({
        username: username.trim(),
        password: password.trim(),
        proxy: proxy || undefined,
      });
      setUsername('');
      setPassword('');
      setProxy('');
      onAdded();
    } catch {
      // Toast lỗi do interceptor bắn — giữ nguyên form để người dùng sửa và thử lại.
    } finally {
      setSubmitting(false);
    }
  };

  const proxyHealth: ProxyHealth = health[proxy] ?? 'unknown';
  const canSubmit = username.trim() && password.trim() && !submitting;

  return (
    <div className="border-line bg-panel rounded-xl border px-6 py-5">
      <h3 className="text-ink mb-1 text-[14px] font-bold">Đăng nhập tài khoản</h3>
      <p className="text-ink-dim mb-4 text-[11px]">
        Xác thực tài khoản HIT có sẵn. Đăng nhập được thì lưu vào hệ thống kèm toàn bộ thông tin nhà
        cái trả về.
      </p>

      <form
        onSubmit={(event) => {
          void handleSubmit(event);
        }}
        className="space-y-4"
      >
        <div>
          <label htmlFor="hit-username" className={LABEL_CLASS}>
            Username <span className="text-danger">*</span>
          </label>
          <input
            id="hit-username"
            type="text"
            value={username}
            onChange={(e) => {
              setUsername(e.target.value);
            }}
            placeholder="Username HIT đã đăng ký"
            required
            className={FIELD_CLASS}
          />
        </div>

        <div>
          <label htmlFor="hit-password" className={LABEL_CLASS}>
            Mật khẩu <span className="text-danger">*</span>
          </label>
          <div className="relative">
            <input
              id="hit-password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
              }}
              placeholder="Mật khẩu HIT"
              required
              className={`${FIELD_CLASS} pr-10`}
            />
            <button
              type="button"
              onClick={() => {
                setShowPassword((v) => !v);
              }}
              className="text-ink-dim hover:text-ink absolute right-2.5 top-1/2 -translate-y-1/2"
              tabIndex={-1}
              aria-label={showPassword ? 'Ẩn mật khẩu' : 'Hiện mật khẩu'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>

        <div>
          <label className={LABEL_CLASS}>Proxy</label>
          <div className="flex items-center gap-1.5">
            <Dropdown
              value={proxy}
              onChange={setProxy}
              placeholder="-- Chọn proxy (bỏ trống = IP máy chủ) --"
              className="flex-1"
              options={options}
            />
            <button
              type="button"
              onClick={checkProxy}
              disabled={!proxy || checking}
              className="border-line bg-canvas text-ink-muted hover:bg-panel-hover hover:text-ink flex shrink-0 items-center gap-1 rounded-lg border px-2.5 py-2.5 text-[11px] font-bold transition-colors disabled:cursor-not-allowed disabled:opacity-50"
              title="Kiểm tra proxy"
            >
              {checking ? <Loader2 size={12} className="animate-spin" /> : <Activity size={12} />}
              Check
            </button>
          </div>
          {proxy && (
            <p className={`text-ink-dim mt-1 text-[10px] ${HEALTH_CLASS[proxyHealth]}`}>
              {HEALTH_LABEL[proxyHealth]}
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={!canSubmit}
          className="bg-success hover:bg-success-hover flex items-center gap-1.5 rounded-lg px-5 py-2.5 text-[13px] font-bold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting && <Loader2 size={14} className="animate-spin" />}
          {submitting ? 'Đang xác thực...' : 'Đăng nhập & Thêm'}
        </button>
      </form>
    </div>
  );
}
